'use client';

import React, { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { TFunction } from '@/i18n/request';
import Button, { ButtonType } from '@/components/Button/Button';
import Filters, { FilterOption } from './Filters';
import IngredientList from './IngredientList';
import styles from './Ingredients.module.scss';
import {
  getIngredients, IngredientsData, IngredientsResponse, Pagination,
} from './api';
import { Locale } from '@/i18n/routing';
import Text from '@/components/Text/Text';

interface IButton {
  id: number,
  title: string,
  type: ButtonType,
  link: string
}

const strongRenderer = (chunks: React.ReactNode) => <strong>{chunks}</strong>;
const PaginationRange = ({ pagination, t }: { pagination: Pagination, t: TFunction }) => {
  const { page, pageSize, total } = pagination;
  if (!pageSize || !total || page < 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <>
      {t.rich('PaginationRange', {
        start,
        end,
        total,
        strong: strongRenderer,
      })}
    </>
  );
};

const IngredientsContent = ({
  data, initialIngredientCategories, initialIngredients,
}: {
  data: { title: string, description: string; button: IButton, secondButton: IButton },
  initialIngredientCategories: FilterOption[]
  initialIngredients: IngredientsResponse,
}) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>(initialIngredientCategories.map((option) => option.value));
  const [filterOptions] = useState<FilterOption[] | []>(initialIngredientCategories);
  const [ingredients, setIngredients] = useState<IngredientsData[]>(initialIngredients.data);
  const [pagination, setPagination] = useState<Pagination | null>(initialIngredients?.meta?.pagination);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const params = useParams();
  const locale = params.locale as Locale;
  const t = useTranslations('ingredients');

  useEffect(() => {
    setLoading(true);

    getIngredients({
      selectedFilters,
      locale,
      page: currentPage,
    }).then((res: IngredientsResponse | null) => {
      if (!res || !res.data) {
        setIngredients([]);
        setPagination(null);
        setLoading(false);
        return;
      }

      if (currentPage === 1) {
        setIngredients(res.data);
      } else {
        setIngredients((prevIngredients) => [...prevIngredients, ...res.data]);
      }

      setPagination(res.meta.pagination);
      setLoading(false);
    });
  }, [selectedFilters, currentPage, locale]);

  const selectFilter = (value: string) => {
    setCurrentPage(1);
    if (value === 'all') {
      setSelectedFilters((prev) => {
        if (prev.length === filterOptions.length) {
          return [];
        }
        return filterOptions.map((option) => option.value);
      });
      return;
    }

    setSelectedFilters((prev) => (prev.includes(value)
      ? prev.filter((v) => v !== value)
      : [...prev, value]));
  };

  const clearFilters = () => {
    setSelectedFilters([]);
  };

  const nextPage = () => {
    if (pagination && currentPage < pagination.pageCount) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const getPaginationButtonText = (type: 'prev' | 'next') => {
    if (!pagination) return type === 'prev' ? 'Show prev' : 'Show next';

    const pageSize = pagination.pageSize || 0;
    const total = pagination.total || 0;

    if (type === 'prev') {
      const prevEnd = (currentPage - 1) * pageSize;
      const prevStart = prevEnd - pageSize + 1;
      return `${data.button.title || 'Show prev'} ${Math.max(prevStart, 1)}-${prevEnd}`;
    } else {
      const nextStart = currentPage * pageSize + 1;
      const nextEnd = Math.min((currentPage + 1) * pageSize, total);
      return `${data.secondButton.title || 'Show next'} ${nextStart}-${nextEnd}`;
    }
  };

  return (
    <section className={styles.ingredients}>
      <div className={styles.contentWrapper}>
        <Filters
          options={filterOptions}
          selected={selectedFilters}
          onToggle={selectFilter}
          clearFilters={clearFilters}
        />
        <div className={styles.ingredientsContent}>
          {loading && <div className={styles.loading} />}
          <Text tag='h2'>{data.title}</Text>
          <Text
            tag='p'
            className={styles.description}
          >
            {data.description}
          </Text>
          <Filters
            options={filterOptions}
            selected={selectedFilters}
            onToggle={selectFilter}
            clearFilters={clearFilters}
            isMobile
          />
          {pagination && (
            <p className={styles.paginationResults}>
              <PaginationRange
                pagination={pagination}
                t={t}
              />
            </p>
          )}
          <IngredientList
            className={styles.girdList}
            key={selectedFilters.join(',')}
            list={ingredients}
            loading={loading}
            currentPage={currentPage}
          />
          {data.button && pagination && (
            <div className={styles.paginationBtns}>
              {currentPage < pagination.pageCount && (
                <Button
                  onClick={nextPage}
                  type={data.secondButton.type}
                  fitContent
                  className={styles.paginationBtn}
                >
                  {getPaginationButtonText('next')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default IngredientsContent;
